/**
 * GYMATE - הצעות בעגלה, באנדלים בדפי מוצר, והחלת קודי הנחה אוטומטית.
 * קובץ חדש. לא משנה שום קובץ קיים ב-theme מלבד השימוש באירועי העגלה הסטנדרטיים.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

const CFG = Object.assign(
  { pairPercent: 10, pairCode: 'GM-PAIR10', samePrefix: 'GM-SAME', enabled: true },
  window.GM_BUNDLES || {}
);

/** זוגות הבאנדל (חייב להתאים ל-snippets/gm-partner.liquid) */
const PAIRS = [
  ['airtrain', 'gympack'],
  ['coreroll', 'flowrun'],
  ['flexbelt', 'airtrain'],
];
const KEYS = ['airtrain', 'coreroll', 'flowrun', 'gympack', 'flexbelt'];

function keyOf(handle = '', title = '') {
  const t = `${handle} ${title}`.toLowerCase();
  if (t.includes('airtrain') || t.includes('נשימה')) return 'airtrain';
  if (t.includes('coreroll') || t.includes('גלגל בטן') || t.includes('גלגל-בטן')) return 'coreroll';
  if (t.includes('flowrun') || t.includes('מכנסי')) return 'flowrun';
  if (t.includes('gympack') || t.includes('תיק מגנטי') || t.includes('תיק-מגנטי')) return 'gympack';
  if (t.includes('flexbelt') || t.includes('חגורת')) return 'flexbelt';
  return '';
}

function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
}

/** מוסיף פריטים לעגלה באותו מנגנון של כפתור "הוספה לסל" של ה-theme (פותח מגירה ומעדכן אותה) */
async function addItems(source, items, productId) {
  const sections = cartSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items, sections: sections.join(',') }),
    });
    const data = await res.json();
    if (data.status) throw new Error(data.description || data.message || 'Add to cart failed');

    const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'gm-bundles',
        sourceId: 'gm-bundles',
        itemCount: items.reduce((s, i) => s + i.quantity, 0),
        productId: String(productId || ''),
        sections: data.sections,
        didError: false,
      },
    });
    return true;
  } catch (e) {
    console.error('[gm-bundles]', e);
    deferred.reject(e);
    return false;
  }
}

/** כפתור "הוספה +" בהצעות בעגלה */
class GmUpsellAdd extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }
  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }
  #onClick = async (event) => {
    const btn = this.querySelector('button');
    if (!btn || btn.disabled || !(event.target instanceof Element) || !event.target.closest('button')) return;
    btn.disabled = true;
    await addItems(this, [{ id: Number(this.dataset.variantId), quantity: 1 }], this.dataset.productId);
    btn.disabled = false;
  };
}

/** כפתור "הוספה לסל" בכרטיסי הבאנדל בדף המוצר */
class GmBundleAdd extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }
  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }
  #currentVariantId() {
    const root = this.closest('.gm-bundles');
    const forms = [...document.querySelectorAll('form[action*="/cart/add"]')];
    for (const form of forms) {
      const input = form.querySelector('input[name="id"], select[name="id"]');
      if (input && input.value && !form.closest('gm-upsell-add')) return Number(input.value);
    }
    return Number(root?.dataset.currentVariant);
  }
  #onClick = async (event) => {
    const btn = this.querySelector('button');
    if (!btn || btn.disabled || !(event.target instanceof Element) || !event.target.closest('button')) return;
    const current = this.#currentVariantId();
    if (!current) return;
    const items =
      this.dataset.kind === 'pair'
        ? [
            { id: current, quantity: 1 },
            { id: Number(this.dataset.partnerVariant), quantity: 1 },
          ]
        : [{ id: current, quantity: 2 }];
    btn.disabled = true;
    await addItems(this, items, current);
    btn.disabled = false;
  };
}

if (!customElements.get('gm-upsell-add')) customElements.define('gm-upsell-add', GmUpsellAdd);
if (!customElements.get('gm-bundle-add')) customElements.define('gm-bundle-add', GmBundleAdd);

/* ------------------------------------------------------------------ */
/* החלת קודי הנחה אוטומטית לפי תוכן העגלה                              */
/* ------------------------------------------------------------------ */
let syncing = false;
let syncAgain = false;

function wantedCodes(cart) {
  const qty = {};
  for (const item of cart.items) {
    const k = keyOf(item.handle, item.product_title);
    if (k) qty[k] = (qty[k] || 0) + item.quantity;
  }
  const codes = [];
  if (PAIRS.some(([a, b]) => qty[a] > 0 && qty[b] > 0)) codes.push(CFG.pairCode);
  for (const k of KEYS) if (qty[k] >= 2) codes.push(`${CFG.samePrefix}-${k.toUpperCase()}`);
  return codes.filter(Boolean);
}

function managedCodes() {
  return [CFG.pairCode, ...KEYS.map((k) => `${CFG.samePrefix}-${k.toUpperCase()}`)].filter(Boolean).map((c) => c.toLowerCase());
}

async function syncDiscounts() {
  if (!CFG.enabled) return;
  if (syncing) {
    syncAgain = true;
    return;
  }
  syncing = true;
  try {
    const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
    const wanted = wantedCodes(cart);
    const managed = managedCodes();
    const existing = (cart.discount_codes || []).map((d) => d.code);
    const others = existing.filter((c) => !managed.includes(c.toLowerCase()));
    const currentManaged = existing.filter((c) => managed.includes(c.toLowerCase()));

    const same =
      wanted.length === currentManaged.length &&
      wanted.every((c) => currentManaged.some((e) => e.toLowerCase() === c.toLowerCase()));
    if (same) return;

    const sections = cartSectionIds();
    await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: [...others, ...wanted].join(',') }),
    });

    for (const id of sections) {
      const inDrawer = !!document.querySelector(`theme-drawer cart-items-component[data-section-id="${id}"]`);
      try {
        await sectionRenderer.renderSection(id, { cache: false, mode: inDrawer ? 'hydration' : 'full' });
      } catch (e) {
        if (e?.name !== 'AbortError') console.warn('[gm-bundles] refresh failed', e);
      }
    }
  } catch (e) {
    console.warn('[gm-bundles] sync failed', e);
  } finally {
    syncing = false;
    if (syncAgain) {
      syncAgain = false;
      syncDiscounts();
    }
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const p = /** @type {any} */ (event).promise;
  if (p && typeof p.then === 'function') p.then(() => setTimeout(syncDiscounts, 150)).catch(() => {});
});
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncDiscounts);
else syncDiscounts();
